import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  GraduationCap, TrendingUp, Target, Award, BookOpen,
  Users, CheckCircle, AlertTriangle, Info, ChevronRight,
  Loader2, BarChart3, Star, Trophy, Building, Search, Plus, Coins
} from 'lucide-react';
import { AI_PERSONAS } from '../constants/aiPersonas';

const AdmissionChances = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    gpa: 3.5,
    gpa_type: 'us_gpa', // 'us_gpa' or 'a_level'
    a_level_grades: '', // e.g., "A*A*A" or "AAB"
    sat_score: 1400,
    act_score: 30,
    toefl_score: 100,
    extracurriculars: 3,
    leadership: 2,
    work_experience: 1,
    essays_strength: 'good',
    recommendations_strength: 'good',
    intended_major: ''
  });
  
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [availableColleges, setAvailableColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Check if user is logged in
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadColleges();
    if (token) {
      loadUserProfile();
    }
  }, []);

  const loadColleges = async () => {
    try {
      const response = await api.get('/colleges');
      setAvailableColleges(response.data);
    } catch (error) {
      console.error('Error loading colleges:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/users/profile');
      if (response.data.academic_info) {
        setProfile(prev => ({
          ...prev,
          gpa: response.data.academic_info.gpa || prev.gpa,
          sat_score: response.data.academic_info.sat_score || prev.sat_score,
          act_score: response.data.academic_info.act_score || prev.act_score,
          toefl_score: response.data.academic_info.toefl_score || prev.toefl_score
        }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const addCollege = (college) => {
    if (!selectedColleges.find(c => c._id === college._id)) {
      setSelectedColleges([...selectedColleges, college]);
    }
    setSearchTerm('');
  };

  const addCustomCollege = () => {
    if (!searchTerm.trim()) return;
    const customCollege = {
      _id: `custom_${Date.now()}`,
      name: searchTerm.trim(),
      location: 'United States',
      acceptance_rate: null,
      is_custom: true
    };
    if (!selectedColleges.find(c => c.name.toLowerCase() === customCollege.name.toLowerCase())) {
      setSelectedColleges([...selectedColleges, customCollege]);
    }
    setSearchTerm('');
  };

  const removeCollege = (collegeId) => {
    setSelectedColleges(selectedColleges.filter(c => c._id !== collegeId));
  };

  const calculateChances = async () => {
    if (selectedColleges.length === 0) {
      toast({
        title: 'No Colleges Selected',
        description: 'Please select at least one college to calculate chances',
        variant: 'destructive'
      });
      return;
    }

    // Check if user is logged in - redirect to pricing if not
    if (!token) {
      toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
      navigate('/pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(
        '/admission/calculate',
        {
          profile,
          college_ids: selectedColleges.map(c => c._id),
          custom_colleges: selectedColleges.filter(c => c.is_custom)
        }
      );
      setResults(response.data.results);
    } catch (error) {
      // If 402 (payment required), redirect to pricing
      if (error.response?.status === 402) {
        toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
        navigate('/pricing');
        return;
      }
      // Calculate locally as fallback
      const localResults = selectedColleges.map(college => {
        const chance = calculateLocalChance(profile, college);
        return {
          college_id: college._id,
          college_name: college.name,
          acceptance_rate: college.acceptance_rate,
          chance_percentage: chance,
          category: getCategory(chance),
          factors: getFactors(profile, college)
        };
      });
      setResults(localResults);
    } finally {
      setLoading(false);
    }
  };

  // Convert A-level grades to GPA equivalent
  const aLevelToGpa = (grades) => {
    if (!grades) return 3.0;
    const gradeValues = { 'A*': 4.0, 'A': 3.7, 'B': 3.3, 'C': 2.7, 'D': 2.0, 'E': 1.0 };
    // Parse grades like "A*AA" or "AAB"
    const gradeList = grades.match(/A\*|[ABCDE]/g) || [];
    if (gradeList.length === 0) return 3.0;
    const total = gradeList.reduce((sum, g) => sum + (gradeValues[g] || 2.0), 0);
    return total / gradeList.length;
  };

  const calculateLocalChance = (profile, college) => {
    let score = 50; // Base score
    
    // GPA impact (max ±25 points)
    let effectiveGpa = profile.gpa;
    if (profile.gpa_type === 'a_level' && profile.a_level_grades) {
      effectiveGpa = aLevelToGpa(profile.a_level_grades);
    }
    const gpaFactor = (effectiveGpa - 3.0) / 1.0 * 25;
    score += gpaFactor;
    
    // SAT impact (max ±20 points)
    if (profile.sat_score) {
      const satFactor = (profile.sat_score - 1200) / 400 * 20;
      score += satFactor;
    }
    
    // ACT impact (max ±15 points) - if no SAT score
    if (!profile.sat_score && profile.act_score) {
      const actFactor = (profile.act_score - 25) / 11 * 15;
      score += actFactor;
    }
    
    // Acceptance rate adjustment
    if (college.acceptance_rate) {
      // Lower acceptance rate = harder to get in
      const rateAdjustment = (college.acceptance_rate - 50) / 50 * 15;
      score += rateAdjustment;
    }
    
    // Extracurriculars (max +10)
    score += Math.min(profile.extracurriculars || 0, 5) * 2;
    
    // Leadership (max +5)
    score += Math.min(profile.leadership || 0, 5) * 1;
    
    // Essays and recommendations
    if (profile.essays_strength === 'excellent') score += 10;
    else if (profile.essays_strength === 'good') score += 5;
    
    if (profile.recommendations_strength === 'excellent') score += 10;
    else if (profile.recommendations_strength === 'good') score += 5;
    
    return Math.max(5, Math.min(95, Math.round(score)));
  };

  const getCategory = (chance) => {
    if (chance >= 70) return 'Safety';
    if (chance >= 40) return 'Match';
    return 'Reach';
  };

  const getFactors = (profile, college) => {
    const factors = [];
    
    // GPA or A-Level assessment
    if (profile.gpa_type === 'a_level' && profile.a_level_grades) {
      const aLevelGpa = aLevelToGpa(profile.a_level_grades);
      if (aLevelGpa >= 3.7) {
        factors.push({ type: 'positive', text: `Strong A-Level grades (${profile.a_level_grades})` });
      } else if (aLevelGpa < 3.0) {
        factors.push({ type: 'negative', text: 'A-Level grades may need improvement' });
      }
    } else {
      if (profile.gpa >= 3.7) {
        factors.push({ type: 'positive', text: 'Strong GPA' });
      } else if (profile.gpa < 3.0) {
        factors.push({ type: 'negative', text: 'GPA below average' });
      }
    }
    
    if (profile.sat_score >= 1400) {
      factors.push({ type: 'positive', text: 'Competitive SAT score' });
    } else if (profile.sat_score && profile.sat_score < 1200) {
      factors.push({ type: 'negative', text: 'SAT score below average' });
    }
    
    if (profile.act_score >= 30) {
      factors.push({ type: 'positive', text: 'Strong ACT score' });
    } else if (profile.act_score && profile.act_score < 24) {
      factors.push({ type: 'negative', text: 'ACT score below average' });
    }
    
    if (profile.extracurriculars >= 4) {
      factors.push({ type: 'positive', text: 'Strong extracurricular involvement' });
    }
    
    if (profile.leadership >= 3) {
      factors.push({ type: 'positive', text: 'Notable leadership experience' });
    }
    
    if (college.acceptance_rate && college.acceptance_rate < 20) {
      factors.push({ type: 'neutral', text: 'Highly selective institution' });
    }
    
    return factors;
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Safety': return 'bg-green-500';
      case 'Match': return 'bg-blue-500';
      case 'Reach': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredColleges = availableColleges.filter(
    c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
         !selectedColleges.find(sc => sc._id === c._id)
  ).slice(0, 8);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 overflow-hidden border-4 border-purple-200 shadow-lg">
            <img 
              src={AI_PERSONAS.WONG.avatar} 
              alt="WONG - Admission Predictor"
              className="w-full h-full object-cover"
            />
          </div>
          <Badge className="mb-4 bg-purple-100 text-purple-700 text-sm px-4 py-1">
            WONG - Admission Predictor
          </Badge>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Meet WONG - Check Your Admission Chances
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get personalized admission probability estimates based on your profile
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Profile Input */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                  Your Academic Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Grade Type Selection */}
                <div>
                  <Label>Grade System</Label>
                  <Select
                    value={profile.gpa_type}
                    onValueChange={(v) => setProfile({...profile, gpa_type: v})}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us_gpa">US GPA (4.0 Scale)</SelectItem>
                      <SelectItem value="a_level">A-Level Grades (UK)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* GPA or A-Level Input */}
                {profile.gpa_type === 'us_gpa' ? (
                  <div>
                    <Label>GPA (out of 4.0)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[profile.gpa]}
                        onValueChange={([v]) => setProfile({...profile, gpa: v})}
                        min={2.0}
                        max={4.0}
                        step={0.1}
                        className="flex-1"
                      />
                      <span className="font-bold text-lg w-12">{profile.gpa.toFixed(1)}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>A-Level Grades (e.g., A*A*A, AAB, ABB)</Label>
                    <Input
                      value={profile.a_level_grades}
                      onChange={(e) => setProfile({...profile, a_level_grades: e.target.value.toUpperCase()})}
                      placeholder="Enter grades like A*AA or AAB"
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use A*, A, B, C, D, E format. Example: A*A*A for three A* grades
                    </p>
                  </div>
                )}

                {/* SAT */}
                <div>
                  <Label>SAT Score (400-1600)</Label>
                  <Input
                    type="number"
                    value={profile.sat_score}
                    onChange={(e) => setProfile({...profile, sat_score: parseInt(e.target.value) || 0})}
                    min={400}
                    max={1600}
                    className="mt-2"
                  />
                </div>

                {/* ACT */}
                <div>
                  <Label>ACT Score (1-36)</Label>
                  <Input
                    type="number"
                    value={profile.act_score}
                    onChange={(e) => setProfile({...profile, act_score: parseInt(e.target.value) || 0})}
                    min={1}
                    max={36}
                    className="mt-2"
                  />
                </div>

                {/* TOEFL */}
                <div>
                  <Label>TOEFL Score (0-120)</Label>
                  <Input
                    type="number"
                    value={profile.toefl_score}
                    onChange={(e) => setProfile({...profile, toefl_score: parseInt(e.target.value) || 0})}
                    min={0}
                    max={120}
                    className="mt-2"
                  />
                </div>

                <Separator />

                {/* Extracurriculars */}
                <div>
                  <Label>Extracurricular Activities (0-10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Slider
                      value={[profile.extracurriculars]}
                      onValueChange={([v]) => setProfile({...profile, extracurriculars: v})}
                      min={0}
                      max={10}
                      step={1}
                      className="flex-1"
                    />
                    <span className="font-bold text-lg w-8">{profile.extracurriculars}</span>
                  </div>
                </div>

                {/* Leadership */}
                <div>
                  <Label>Leadership Roles (0-5)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Slider
                      value={[profile.leadership]}
                      onValueChange={([v]) => setProfile({...profile, leadership: v})}
                      min={0}
                      max={5}
                      step={1}
                      className="flex-1"
                    />
                    <span className="font-bold text-lg w-8">{profile.leadership}</span>
                  </div>
                </div>

                {/* Essays */}
                <div>
                  <Label>Essays Quality</Label>
                  <Select
                    value={profile.essays_strength}
                    onValueChange={(v) => setProfile({...profile, essays_strength: v})}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weak">Needs Improvement</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="excellent">Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Recommendations */}
                <div>
                  <Label>Recommendations Quality</Label>
                  <Select
                    value={profile.recommendations_strength}
                    onValueChange={(v) => setProfile({...profile, recommendations_strength: v})}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weak">Needs Improvement</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="excellent">Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Middle: College Selection */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  Select Colleges
                </CardTitle>
                <CardDescription>
                  Add colleges to check your chances
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search colleges..."
                    className="pl-9"
                  />
                  {searchTerm && (
                    <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 z-10 max-h-60 overflow-y-auto">
                      {filteredColleges.map(college => (
                        <button
                          key={college._id}
                          onClick={() => addCollege(college)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-sm">{college.name}</p>
                            <p className="text-xs text-gray-500">{college.location}</p>
                          </div>
                          <Plus className="h-4 w-4 text-gray-400" />
                        </button>
                      ))}
                      {/* Always show "Add custom" option */}
                      <button
                        onClick={addCustomCollege}
                        className="w-full text-left px-3 py-2.5 hover:bg-purple-50 flex items-center justify-between border-t border-gray-100 bg-gray-50"
                        data-testid="add-custom-college"
                      >
                        <div>
                          <p className="font-medium text-sm text-purple-700">+ Add "{searchTerm}"</p>
                          <p className="text-xs text-gray-500">Can't find your college? Add it manually</p>
                        </div>
                        <Plus className="h-4 w-4 text-purple-500" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Selected Colleges */}
                <div className="space-y-2">
                  {selectedColleges.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No colleges selected</p>
                      <p className="text-sm">Search and add colleges above</p>
                    </div>
                  ) : (
                    selectedColleges.map(college => (
                      <div
                        key={college._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{college.name}</p>
                          <p className="text-xs text-gray-500">
                            {college.is_custom ? 'Custom added' : college.acceptance_rate ? `${college.acceptance_rate}% acceptance rate` : college.location || 'United States'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCollege(college._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Calculate Button */}
                <Button
                  onClick={calculateChances}
                  disabled={loading || selectedColleges.length === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</>
                  ) : (
                    <><BarChart3 className="h-4 w-4 mr-2" /> Calculate Chances</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card>
              <CardContent className="pt-4">
                <h4 className="font-semibold mb-3">Category Legend</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500" />
                    <span className="text-sm"><strong>Safety</strong> (70%+) - Likely to get in</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="text-sm"><strong>Match</strong> (40-70%) - Good chance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-500" />
                    <span className="text-sm"><strong>Reach</strong> (&lt;40%) - Challenging but possible</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  Your Chances
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!results ? (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Select colleges and calculate</p>
                    <p className="text-sm">to see your admission chances</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {results.map((result, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold">{result.college_name}</h4>
                            <p className="text-xs text-gray-500">
                              {result.acceptance_rate}% overall acceptance
                            </p>
                          </div>
                          <Badge className={getCategoryColor(result.category)}>
                            {result.category}
                          </Badge>
                        </div>
                        
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Your Chance</span>
                            <span className="font-bold">{result.chance_percentage}%</span>
                          </div>
                          <Progress value={result.chance_percentage} className="h-3" />
                        </div>
                        
                        {result.factors && result.factors.length > 0 && (
                          <div className="space-y-1">
                            {result.factors.map((factor, fidx) => (
                              <div
                                key={fidx}
                                className={`flex items-center gap-2 text-xs ${
                                  factor.type === 'positive' ? 'text-green-600' :
                                  factor.type === 'negative' ? 'text-red-600' : 'text-gray-600'
                                }`}
                              >
                                {factor.type === 'positive' ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : factor.type === 'negative' ? (
                                  <AlertTriangle className="h-3 w-3" />
                                ) : (
                                  <Info className="h-3 w-3" />
                                )}
                                {factor.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Disclaimer</p>
                    <p>
                      These are estimated probabilities based on historical data and your profile. 
                      Actual admission decisions depend on many factors including essays, 
                      interviews, and institutional priorities.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionChances;
